/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Tenant Data Hooks - React Query for SuperAdmin purge / backup / restore
 * ============================================================================
 *
 * Jobs are polled every 2 s while non-terminal and stop polling as soon as the
 * job (or every job in a list page) reaches COMPLETED, COMPLETED_WITH_WARNINGS
 * or FAILED. Toasts are raised by the pages (onError), not here.
 *
 * ============================================================================
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantDataService } from '@/services/superadmin/tenant-data.service';
import { ApiError, PaginatedResponse } from '@/services/api/types';
import {
  isTerminalStatus,
  type PurgePreview,
  type PurgePreviewRequest,
  type PurgeRequest,
  type RestorePreview,
  type RestoreRequest,
  type RestoreUploadResult,
  type TenantDataJob,
  type TenantDataJobsParams,
} from '@/types/tenant-data.types';

// ── Query keys ───────────────────────────────────────────────────────────────

export const tenantDataKeys = {
  all: ['tenantData'] as const,
  jobs: (p: TenantDataJobsParams) => [...tenantDataKeys.all, 'jobs', p] as const,
  job: (id: string) => [...tenantDataKeys.all, 'job', id] as const,
};

// ── Polling helpers ──────────────────────────────────────────────────────────

export const JOB_POLL_INTERVAL_MS = 2000;

/** Poll a single job while it is running; stop once it is terminal or absent. */
export function jobRefetchInterval(job?: TenantDataJob | null): number | false {
  if (!job || isTerminalStatus(job.status)) {
    return false;
  }
  return JOB_POLL_INTERVAL_MS;
}

/** Poll a job list page while at least one of its jobs is still running. */
export function jobsRefetchInterval(
  page?: PaginatedResponse<TenantDataJob> | null
): number | false {
  const hasRunning = (page?.content ?? []).some((job) => !isTerminalStatus(job.status));
  return hasRunning ? JOB_POLL_INTERVAL_MS : false;
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function useTenantDataJobs(
  params: TenantDataJobsParams,
  options?: { enabled?: boolean }
) {
  return useQuery<PaginatedResponse<TenantDataJob>, ApiError>({
    queryKey: tenantDataKeys.jobs(params),
    queryFn: () => tenantDataService.listJobs(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => jobsRefetchInterval(query.state.data),
  });
}

export function useTenantDataJob(jobId: string | null) {
  return useQuery<TenantDataJob, ApiError>({
    queryKey: tenantDataKeys.job(jobId ?? ''),
    queryFn: () => tenantDataService.getJob(jobId as string),
    enabled: !!jobId,
    staleTime: 0,
    refetchInterval: (query) => jobRefetchInterval(query.state.data),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function usePurgePreview() {
  return useMutation<PurgePreview, ApiError, PurgePreviewRequest>({
    mutationFn: (req) => tenantDataService.previewPurge(req),
  });
}

/** Variable is the target orgId; posts `{ orgId }`. */
export function useStartBackup() {
  const queryClient = useQueryClient();
  return useMutation<TenantDataJob, ApiError, string>({
    mutationFn: (orgId) => tenantDataService.startBackup(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDataKeys.all });
    },
  });
}

export function useStartPurge() {
  const queryClient = useQueryClient();
  return useMutation<TenantDataJob, ApiError, PurgeRequest>({
    mutationFn: (req) => tenantDataService.startPurge(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDataKeys.all });
    },
  });
}

export function useUploadBackup() {
  return useMutation<RestoreUploadResult, ApiError, File>({
    mutationFn: (file) => tenantDataService.uploadBackup(file),
  });
}

export function useRestorePreview() {
  return useMutation<RestorePreview, ApiError, RestoreRequest>({
    mutationFn: (req) => tenantDataService.previewRestore(req),
  });
}

export function useStartRestore() {
  const queryClient = useQueryClient();
  return useMutation<TenantDataJob, ApiError, RestoreRequest>({
    mutationFn: (req) => tenantDataService.startRestore(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDataKeys.all });
    },
  });
}

/** Variable is the job id whose backup file is deleted. */
export function useDeleteBackup() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => tenantDataService.deleteBackup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDataKeys.all });
    },
  });
}

export function useDownloadBackup() {
  return useMutation<void, ApiError, { id: string; fallbackName: string }>({
    mutationFn: ({ id, fallbackName }) => tenantDataService.downloadBackup(id, fallbackName),
  });
}
